import type { ApiResponse } from 'apisauce';
import type { ResponseModel } from '../shared/interfaces/response-model.interface';
import api from '../shared/services/utils/api/api.service';
import type { ErrorInfo } from '../shared/interfaces/error-info.interface';
import errorHelper from '../shared/services/utils/api/error-helper.service';
import type { PackageModel, FurnishingModel } from './types';
import { getAllTypeOfUnit } from '../shared/services/data/lookups.data';
export type FurnishingRequestStatus = 'new' | 'ready' | 'in_progress' | 'won' | 'lost' | 'complete';

export const MODULE_NAME = 'FURNISHING_MODULE';

// Furnishing Request types and API
export interface FurnishingRequestResponse {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  source?: string;
  typeOfUnit?: string;
  unitTypeGroup?: number;
  area?: string;
  packageName?: string;
  modelName?: string;
  addOns?: string[];
  comments?: (| {
        text: string;
        author?: string;
        createdAt?: string;
      }
    | string)[];
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  price?: string;
  assignedToId?: string;
}

export interface FurnishingRequestDetailsUpdatePayload {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  source?: string;
  typeOfUnit?: string;
  unitTypeGroup?: string | number;
  areaName?: string;
  addons?: string[];
  packageId?: number;
  modelId?: number;
  price?: number;
}

export interface FurnishingRequestsListResponse {
  data: FurnishingRequestResponse[];
  count: number;
}

export interface FurnishingRequestsQuery {
  offset?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export interface FurnishingRequestComment {
  text: string;
  author?: string;
  createdAt?: string;
}

export interface UpdateFurnishingRequestPayload {
  status?: FurnishingRequestStatus;
  assignedToId?: string;
  assignedToEmail?: string;
  assignedToSalesId?: string;
  assignedToSalesEmail?: string;
  comments?: FurnishingRequestComment[];
}

export type UpdateFurnishingRequestPayloadExtended =
  | UpdateFurnishingRequestPayload
  | (UpdateFurnishingRequestPayload & FurnishingRequestDetailsUpdatePayload);

export function getAllFurnishingRequests(
  query: FurnishingRequestsQuery = {}
): Promise<FurnishingRequestsListResponse | ErrorInfo> {
  const { offset = 1, limit = 10, status, search } = query;
  const params: Record<string, any> = { offset, limit };
  if (status) params.status = status;
  if (search) params.search = search;

  return api
    .furnishingRequestsGet(params)
    .then((response: ApiResponse<ResponseModel<FurnishingRequestResponse[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;

        if (data?.data) {
          return {
            data: data.data,
            count: data.count ?? data.total ?? data.data.length
          };
        }

        return errorHelper(0, '', MODULE_NAME, (api.furnishingRequestsGet as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.furnishingRequestsGet as any).displayName
      );
    });
}

export function updateFurnishingRequest(
  id: string,
  payload: UpdateFurnishingRequestPayloadExtended
): Promise<FurnishingRequestResponse | ErrorInfo> {
  const data = { set: payload, unset: {} };
  return api
    .furnishingRequestUpdate(id, data)
    .then((response: ApiResponse<ResponseModel<FurnishingRequestResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;

        if (data?.data) {
          return data.data;
        }

        return errorHelper(0, '', MODULE_NAME, (api.furnishingRequestUpdate as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.furnishingRequestUpdate as any).displayName
      );
    });
}

export interface NumberOfRoomsOption {
  value: string;
  label: string;
}

export interface HomeTypeOption {
  value: string;
  label: string;
}

export function getHomeTypes(): Promise<HomeTypeOption[] | ErrorInfo> {
  return getAllTypeOfUnit(MODULE_NAME).then((response) => {
    if (Array.isArray(response)) {
      return response.map((item) => ({
        value: item.id || item.name,
        label: item.name
      }));
    }
    return response as ErrorInfo;
  });
}

export function getFurnishingNumberOfRooms(): Promise<NumberOfRoomsOption[] | ErrorInfo> {
  return api.furnishingUnitTypes().then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
    if (response.ok) {
      const { data } = response;

      if (data?.data) {
        const res = data.data.map((item: any) => ({
          value: String(item.id),
          label: item.name
        }));
        return res;
      } else {
        return errorHelper(0, '', MODULE_NAME, 'getNumberOfRooms');
      }
    } else {
      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        'getNumberOfRooms'
      );
    }
  });
}

export function getFurnishingNumberOfRoomsWebsite(): Promise<NumberOfRoomsOption[] | ErrorInfo> {
  return api
    .furnishingUnitTypesWebsite()
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        console.log('response', response);
        if (data?.data) {
          const res = data.data.map((item: any) => ({
            value: String(item.id),
            label: item.name
          }));
          return res;
        } else {
          return errorHelper(0, '', MODULE_NAME, 'getNumberOfRooms');
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          'getNumberOfRooms'
        );
      }
    });
}

export interface ShowRoomResponse {
  id: string;
  name: string;
  nameAr: string;
  images: string[];
  modelId: string;
  order?: number;
}

export function getAllShowRooms(): Promise<ShowRoomResponse[] | ErrorInfo> {
  return api
    .furnishingShowRoomsGet()
    .then((response: ApiResponse<ResponseModel<ShowRoomResponse[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingShowRoomsGet as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingShowRoomsGet as any).displayName
        );
      }
    });
}

export function getAllModels(): Promise<FurnishingModel[] | ErrorInfo> {
  return api
    .furnishingModelsGet()
    .then((response: ApiResponse<ResponseModel<FurnishingModel[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingModelsGet as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingModelsGet as any).displayName
        );
      }
    });
}

export interface FurnishingOffer {
  id: string;
  packageId: string;
  modelId: string;
  unitTypeGroupId: string;
  price: number;
  addons?: string;
}

export function getAllOffers(): Promise<FurnishingOffer[] | ErrorInfo> {
  return api
    .furnishingOffersGet()
    .then((response: ApiResponse<ResponseModel<FurnishingOffer[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingOffersGet as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingOffersGet as any).displayName
        );
      }
    });
}

export function getAllFurnishingPackages(): Promise<PackageModel[] | ErrorInfo> {
  return api
    .furnishingPackagesGet()
    .then((response: ApiResponse<ResponseModel<PackageModel[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingPackagesGet as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPackagesGet as any).displayName
        );
      }
    });
}

export interface FurnishingRequestStatusEvent {
  furnishingRequestId: number;
  from: FurnishingRequestStatus;
  to: FurnishingRequestStatus;
  changedById?: string;
  changedByEmail?: string;
  createdAt: string;
  updatedAt?: string;
  id: number;
}

export interface FurnishingRequestStatusEventsApiResponse {
  status: number;
  success: boolean;
  message?: string;
  data: FurnishingRequestStatusEvent[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export function getFurnishingRequestStatusEvents(
  furnishingRequestId: string
): Promise<FurnishingRequestStatusEventsApiResponse | ErrorInfo> {
  return api
    .furnishingRequestStatusEventsGet(furnishingRequestId)
    .then(
      (
        response: ApiResponse<ResponseModel<FurnishingRequestStatusEventsApiResponse>, ErrorInfo>
      ) => {
        if (response.ok) {
          const { data } = response;
          if (data?.data) {
            return data.data;
          }
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingRequestStatusEventsGet as any).displayName ||
              'furnishingRequestStatusEventsGet'
          );
        }

        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingRequestStatusEventsGet as any).displayName ||
            'furnishingRequestStatusEventsGet'
        );
      }
    );
}

export async function addNewFurnishingPackage(formData: any): Promise<PackageModel | ErrorInfo> {
  const data = new FormData();
  data.append('asset', formData.asset);
  data.append('name', formData.name);
  data.append('nameAr', formData.nameAr);
  data.append('description', formData.description);
  data.append('descriptionAr', formData.descriptionAr);
  data.append('order', String(formData.order));

  return api
    .furnishingPackageCreate(data)
    .then((response: ApiResponse<ResponseModel<PackageModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingPackageCreate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPackageCreate as any).displayName
        );
      }
    });
}

export async function updateFurnishingPackage(
  id: string,
  formData: any
): Promise<PackageModel | ErrorInfo> {
  const data = {
    set: {
      name: formData.name,
      nameAr: formData.nameAr,
      description: formData.description,
      descriptionAr: formData.descriptionAr,
      order: Number(formData.order)
    }
  };

  return api
    .furnishingPackageUpdate(id, data)
    .then((response: ApiResponse<ResponseModel<PackageModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingPackageUpdate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPackageUpdate as any).displayName
        );
      }
    });
}

export async function deleteFurnishingPackage(id: string): Promise<PackageModel | ErrorInfo> {
  return api.furnishingPackageDelete(id).then((response) => {
    if (response.ok) {
      const { data } = response;
      if (data?.data) {
        return data.data;
      } else {
        return errorHelper(0, '', MODULE_NAME, (api.furnishingPackageDelete as any).displayName);
      }
    } else {
      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.furnishingPackageDelete as any).displayName
      );
    }
  });
}

export async function saveModel(modelData: any): Promise<any | ErrorInfo> {
  const data = new FormData();
  data.append('name', modelData.name);
  data.append('nameAr', modelData.nameAr);
  data.append('color', modelData.color);
  data.append('asset', modelData.asset);
  data.append('packageId', modelData.packageId);
  data.append('order', String(modelData.order));

  return api
    .furnishingModelCreate(data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingModelCreate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingModelCreate as any).displayName
        );
      }
    });
}

export async function updateModel(modelId: string, modelData: any): Promise<any | ErrorInfo> {
  const payload = {
    set: {
      name: modelData.name,
      nameAr: modelData.nameAr,
      color: modelData.color,
      order: Number(modelData.order)
    },
    unset: {}
  };

  return api
    .furnishingModelUpdate(modelId, payload)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingModelUpdate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingModelUpdate as any).displayName
        );
      }
    });
}

export async function deleteModel(modelId: string): Promise<any | ErrorInfo> {
  return api
    .furnishingModelDelete(modelId)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingModelDelete as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingModelDelete as any).displayName
        );
      }
    });
}

export async function saveShowRoom(showRoomData: any): Promise<any | ErrorInfo> {
  const data = new FormData();
  data.append('modelId', showRoomData.modelId);
  data.append('name', showRoomData.name);
  data.append('nameAr', showRoomData.nameAr);
  data.append('order', String(showRoomData.order));
  showRoomData.images?.forEach((image: File) => {
    data.append('asset', image);
  });

  return api
    .furnishingRoomCreate(data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingRoomCreate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingRoomCreate as any).displayName
        );
      }
    });
}

export async function deleteShowRoom(showRoomId: string): Promise<any | ErrorInfo> {
  return api
    .furnishingRoomDelete(showRoomId)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingRoomDelete as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingRoomDelete as any).displayName
        );
      }
    });
}

export async function updateShowRoom(
  showRoomId: string,
  showRoomData: { name?: string; nameAr?: string; order?: number }
): Promise<any | ErrorInfo> {
  const payload: { set: Record<string, any>; unset: Record<string, string> } = {
    set: {},
    unset: {}
  };

  if (showRoomData.name !== undefined) {
    payload.set.name = showRoomData.name;
  }
  if (showRoomData.nameAr !== undefined) {
    payload.set.nameAr = showRoomData.nameAr;
  }
  if (showRoomData.order !== undefined) {
    payload.set.order = showRoomData.order;
  }

  return api
    .furnishingRoomUpdate(showRoomId, payload)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingRoomUpdate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingRoomUpdate as any).displayName
        );
      }
    });
}

// ItemSpec operations
export interface ItemSpecResponse {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  image: string;
  showRoomId: string;
  order?: number;
}

export function getAllItemSpecs(): Promise<ItemSpecResponse[] | ErrorInfo> {
  return api
    .furnishingItemSpecsGet()
    .then((response: ApiResponse<ResponseModel<ItemSpecResponse[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingItemSpecsGet as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingItemSpecsGet as any).displayName
        );
      }
    });
}

export async function deleteItemSpec(itemSpecId: string): Promise<any | ErrorInfo> {
  return api
    .furnishingItemSpecDelete(itemSpecId)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingItemSpecDelete as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingItemSpecDelete as any).displayName
        );
      }
    });
}

export async function saveItemSpec(itemSpecData: any): Promise<any | ErrorInfo> {
  const data = new FormData();
  data.append('showRoomId', itemSpecData.showRoomId);
  data.append('name', itemSpecData.name);
  data.append('nameAr', itemSpecData.nameAr);
  data.append('description', itemSpecData.description);
  data.append('descriptionAr', itemSpecData.descriptionAr);
  data.append('asset', itemSpecData.image);
  data.append('order', String(itemSpecData.order));

  return api
    .furnishingItemSpecCreate(data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingItemSpecCreate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingItemSpecCreate as any).displayName
        );
      }
    });
}

export async function updateItemSpec(
  itemSpecId: string,
  itemSpecData: any
): Promise<any | ErrorInfo> {
  const data = {
    set: {
      name: itemSpecData.name,
      nameAr: itemSpecData.nameAr,
      description: itemSpecData.description,
      descriptionAr: itemSpecData.descriptionAr,
      order: Number(itemSpecData.order)
    }
  };

  return api
    .furnishingItemSpecUpdate(itemSpecId, data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingItemSpecUpdate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingItemSpecUpdate as any).displayName
        );
      }
    });
}

// Image update operations
export async function updateModelImage(modelId: string, imageFile: File): Promise<any | ErrorInfo> {
  const data = new FormData();
  data.append('asset', imageFile);

  return api
    .furnishingModelImageUpdate(modelId, data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingModelImageUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingModelImageUpdate as any).displayName
        );
      }
    });
}

export async function updatePackageImage(
  packageId: string,
  imageFile: File
): Promise<any | ErrorInfo> {
  const data = new FormData();
  data.append('asset', imageFile);

  return api
    .furnishingPackageImageUpdate(packageId, data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPackageImageUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPackageImageUpdate as any).displayName
        );
      }
    });
}

export async function updateShowRoomImages(
  showRoomId: string,
  imageFiles: File[],
  existingImages: string[] = []
): Promise<any | ErrorInfo> {
  const data = new FormData();
  imageFiles.forEach((file) => {
    data.append('asset', file);
  });

  data.append('images', JSON.stringify(existingImages));

  return api
    .furnishingRoomImageUpdate(showRoomId, data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingRoomImageUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingRoomImageUpdate as any).displayName
        );
      }
    });
}

export async function updateItemSpecImage(
  itemSpecId: string,
  imageFile: File
): Promise<any | ErrorInfo> {
  const data = new FormData();
  data.append('asset', imageFile);

  return api
    .furnishingItemSpecImageUpdate(itemSpecId, data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingItemSpecImageUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingItemSpecImageUpdate as any).displayName
        );
      }
    });
}

export interface PortfolioResponse {
  id: string;
  name: string;
  nameAr: string;
  location: string;
  year: number;
  timeline: number;
  homeType: string;
  packageId: string;
  numberOfBedrooms: number;
  minPrice: number;
  maxPrice: number;
  description: string;
  descriptionAr: string;
  image: string;
  isPublished?: boolean;
  modelId?: string;
  otherModel?: string;
  order?: number;
}

export function getAllPortfolios(): Promise<PortfolioResponse[] | ErrorInfo> {
  return api
    .furnishingPortfolioGet()
    .then((response: ApiResponse<ResponseModel<PortfolioResponse[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingPortfolioGet as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioGet as any).displayName
        );
      }
    });
}

export interface CreatePortfolioData {
  name: string;
  nameAr: string;
  location: string;
  year: number;
  timeline: number;
  homeType: string;
  packageId: string;
  numberOfBedrooms: number;
  minPrice: number;
  maxPrice: number;
  description: string;
  descriptionAr: string;
  asset: File;
  modelId?: string;
  otherModel?: string;
  order?: number;
}

export async function createPortfolio(
  portfolioData: CreatePortfolioData
): Promise<PortfolioResponse | ErrorInfo> {
  const data = new FormData();
  data.append('name', portfolioData.name);
  data.append('nameAr', portfolioData.nameAr);
  data.append('location', portfolioData.location);
  data.append('year', String(portfolioData.year));
  data.append('timeline', String(portfolioData.timeline));
  data.append('homeType', portfolioData.homeType);
  data.append('packageId', portfolioData.packageId);
  data.append('numberOfBedrooms', String(portfolioData.numberOfBedrooms));
  data.append('minPrice', String(portfolioData.minPrice));
  data.append('maxPrice', String(portfolioData.maxPrice));
  data.append('description', portfolioData.description);
  data.append('descriptionAr', portfolioData.descriptionAr);
  data.append('asset', portfolioData.asset);
  if (portfolioData.modelId) {
    data.append('modelId', portfolioData.modelId);
  }
  if (portfolioData.otherModel) {
    data.append('otherModel', portfolioData.otherModel);
  }
  if (portfolioData.order !== undefined) {
    data.append('order', String(portfolioData.order));
  }

  return api
    .furnishingPortfolioCreate(data)
    .then((response: ApiResponse<ResponseModel<PortfolioResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioCreate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioCreate as any).displayName
        );
      }
    });
}

export async function deletePortfolio(id: string): Promise<PortfolioResponse | ErrorInfo> {
  return api
    .furnishingPortfolioDelete(id)
    .then((response: ApiResponse<ResponseModel<PortfolioResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioDelete as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioDelete as any).displayName
        );
      }
    });
}

export interface UpdatePortfolioData {
  name?: string;
  nameAr?: string;
  location?: string;
  year?: number;
  homeType?: string;
  package?: string;
  numberOfBedrooms?: number;
  minPrice?: number;
  maxPrice?: number;
  description?: string;
  descriptionAr?: string;
  isPublished?: boolean;
  modelId?: string;
  otherModel?: string;
  order?: number;
  unsetModelId?: boolean;
}

export async function updatePortfolio(
  id: string,
  portfolioData: UpdatePortfolioData
): Promise<PortfolioResponse | ErrorInfo> {
  const payload: { set: Record<string, any>; unset: Record<string, boolean> } = {
    set: {},
    unset: {}
  };

  if (portfolioData.name !== undefined) payload.set.name = portfolioData.name;
  if (portfolioData.nameAr !== undefined) payload.set.nameAr = portfolioData.nameAr;
  if (portfolioData.location !== undefined) payload.set.location = portfolioData.location;
  if (portfolioData.year !== undefined) payload.set.year = portfolioData.year;
  if (portfolioData.homeType !== undefined) payload.set.homeType = portfolioData.homeType;
  if (portfolioData.package !== undefined) payload.set.package = portfolioData.package;
  if (portfolioData.numberOfBedrooms !== undefined)
    payload.set.numberOfBedrooms = portfolioData.numberOfBedrooms;
  if (portfolioData.minPrice !== undefined) payload.set.minPrice = portfolioData.minPrice;
  if (portfolioData.maxPrice !== undefined) payload.set.maxPrice = portfolioData.maxPrice;
  if (portfolioData.description !== undefined) payload.set.description = portfolioData.description;
  if (portfolioData.descriptionAr !== undefined)
    payload.set.descriptionAr = portfolioData.descriptionAr;

  if (portfolioData.isPublished !== undefined) payload.set.isPublished = portfolioData.isPublished;
  if (portfolioData.modelId !== undefined) payload.set.modelId = portfolioData.modelId;
  if (portfolioData.otherModel !== undefined) payload.set.otherModel = portfolioData.otherModel;
  if (portfolioData.order !== undefined) payload.set.order = portfolioData.order;

  // If unsetModelId is true, add modelId to unset
  if (portfolioData.unsetModelId === true) {
    payload.unset.modelId = true;
    payload.set.modelId = undefined;
  }

  return api
    .furnishingPortfolioUpdate(id, payload)
    .then((response: ApiResponse<ResponseModel<PortfolioResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioUpdate as any).displayName
        );
      }
    });
}

export async function updatePortfolioImage(
  id: string,
  imageFile: File
): Promise<PortfolioResponse | ErrorInfo> {
  const data = new FormData();
  data.append('asset', imageFile);

  return api
    .furnishingPortfolioImageUpdate(id, data)
    .then((response: ApiResponse<ResponseModel<PortfolioResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioImageUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioImageUpdate as any).displayName
        );
      }
    });
}

export interface PortfolioRoomResponse {
  id: string;
  portfolioId: string;
  name: string;
  images: string[];
  order?: number;
}

export interface PortfolioReviewResponse {
  id: string;
  portfolioId: string;
  name: string;
  description: string;
}

export function getAllPortfolioRooms(): Promise<PortfolioRoomResponse[] | ErrorInfo> {
  return api
    .furnishingPortfolioRoomsGet()
    .then((response: ApiResponse<ResponseModel<PortfolioRoomResponse[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioRoomsGet as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioRoomsGet as any).displayName
        );
      }
    });
}

export interface CreatePortfolioRoomData {
  portfolioId: string;
  name: string;
  assets: File[];
  order?: number;
}

export async function createPortfolioRoom(
  roomData: CreatePortfolioRoomData
): Promise<PortfolioRoomResponse | ErrorInfo> {
  const data = new FormData();
  data.append('portfolioId', roomData.portfolioId);
  data.append('name', roomData.name);
  if (roomData.order !== undefined) {
    data.append('order', String(roomData.order));
  }
  roomData?.assets?.forEach((file) => {
    data.append('asset', file);
  });

  return api
    .furnishingPortfolioRoomsCreate(data)
    .then((response: ApiResponse<ResponseModel<PortfolioRoomResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioRoomsCreate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioRoomsCreate as any).displayName
        );
      }
    });
}

export interface UpdatePortfolioRoomData {
  name?: string;
  order?: number;
}

export async function updatePortfolioRoom(
  roomId: string,
  roomData: UpdatePortfolioRoomData
): Promise<PortfolioRoomResponse | ErrorInfo> {
  const payload: { set: Record<string, any>; unset: Record<string, boolean> } = {
    set: {},
    unset: {}
  };

  if (roomData.name !== undefined) payload.set.name = roomData.name;
  if (roomData.order !== undefined) payload.set.order = roomData.order;

  return api
    .furnishingPortfolioRoomsUpdate(roomId, payload)
    .then((response: ApiResponse<ResponseModel<PortfolioRoomResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioRoomsUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioRoomsUpdate as any).displayName
        );
      }
    });
}

export async function updatePortfolioRoomImages(
  roomId: string,
  newImageFiles: File[],
  existingImages: string[] = []
): Promise<PortfolioRoomResponse | ErrorInfo> {
  const data = new FormData();
  newImageFiles.forEach((file) => {
    data.append('asset', file);
  });

  data.append('images', JSON.stringify(existingImages));

  return api
    .furnishingPortfolioRoomsImageUpdate(roomId, data)
    .then((response: ApiResponse<ResponseModel<PortfolioRoomResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioRoomsImageUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioRoomsImageUpdate as any).displayName
        );
      }
    });
}

type OfferPayload = {
  packageId: string;
  modelId: string;
  unitTypeGroupId: string;
  price: number;
  addons?: string;
};

export async function savePrices(prices: OfferPayload[]): Promise<OfferPayload[] | ErrorInfo> {
  return api
    .furnishingPricesCreate({ prices } as any)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data as OfferPayload[];
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingPricesCreate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPricesCreate as any).displayName
        );
      }
    });
}

export async function updatePrice(
  offerId: string,
  priceData: OfferPayload
): Promise<{ id: string; unitTypeGroupId: string; price: number; addons?: string } | ErrorInfo> {
  const data = {
    set: {
      ...priceData
    }
  };
  return api
    .furnishingOffersUpdate(offerId, data as any)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data as {
            id: string;
            packageId: string;
            modelId: string;
            unitTypeGroupId: string;
            price: number;
            addons?: string;
          };
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingOffersUpdate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingOffersUpdate as any).displayName
        );
      }
    });
}

export async function deleteOffer(offerId: string): Promise<any | ErrorInfo> {
  return api
    .furnishingOffersDelete(offerId)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingOffersDelete as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingOffersDelete as any).displayName
        );
      }
    });
}

// Portfolio Review operations
export function getAllPortfolioReviews(): Promise<PortfolioReviewResponse[] | ErrorInfo> {
  return api
    .furnishingPortfolioReviewsGet()
    .then((response: ApiResponse<ResponseModel<PortfolioReviewResponse[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioReviewsGet as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioReviewsGet as any).displayName
        );
      }
    });
}

export interface CreatePortfolioReviewData {
  portfolioId: string;
  name: string;
  description: string;
}

export async function createPortfolioReview(
  reviewData: CreatePortfolioReviewData
): Promise<PortfolioReviewResponse | ErrorInfo> {
  return api
    .furnishingPortfolioReviewCreate(reviewData)
    .then((response: ApiResponse<ResponseModel<PortfolioReviewResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioReviewCreate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioReviewCreate as any).displayName
        );
      }
    });
}

export interface UpdatePortfolioReviewData {
  name?: string;
  description?: string;
  portfolioId?: string;
}

export async function updatePortfolioReview(
  reviewId: string,
  reviewData: UpdatePortfolioReviewData
): Promise<PortfolioReviewResponse | ErrorInfo> {
  const payload: { set: Record<string, any> } = {
    set: {}
  };

  if (reviewData.name !== undefined) payload.set.name = reviewData.name;
  if (reviewData.description !== undefined) payload.set.description = reviewData.description;
  if (reviewData.portfolioId !== undefined) payload.set.portfolioId = reviewData.portfolioId;

  return api
    .furnishingPortfolioReviewUpdate(reviewId, payload)
    .then((response: ApiResponse<ResponseModel<PortfolioReviewResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioReviewUpdate as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioReviewUpdate as any).displayName
        );
      }
    });
}

export async function deletePortfolioReview(
  reviewId: string
): Promise<PortfolioReviewResponse | ErrorInfo> {
  return api
    .furnishingPortfolioReviewDelete(reviewId)
    .then((response: ApiResponse<ResponseModel<PortfolioReviewResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(
            0,
            '',
            MODULE_NAME,
            (api.furnishingPortfolioReviewDelete as any).displayName
          );
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingPortfolioReviewDelete as any).displayName
        );
      }
    });
}

// AddOns operations
export interface AddOnItemPayload {
  name: string;
  nameAr: string;
  price: number;
  order?: number;
}

export interface AddOnPayload {
  packageId: string;
  unitTypeId: string;
  addons: AddOnItemPayload[];
}

export interface AddOnResponse {
  id: string;
  packageId: string;
  unitTypeId: string;
  addons: {
    id?: string;
    name: string;
    nameAr: string;
    price: number;
  }[];
}

export function getAllAddOns(): Promise<AddOnResponse[] | ErrorInfo> {
  return api
    .furnishingAddOnsGet()
    .then((response: ApiResponse<ResponseModel<AddOnResponse[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingAddOnsGet as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingAddOnsGet as any).displayName
        );
      }
    });
}

export async function saveAddOns(addOnData: AddOnPayload): Promise<AddOnResponse | ErrorInfo> {
  const convertedData = {
    ...addOnData,
    packageId: Number(addOnData.packageId)
  };

  return api
    .furnishingAddOnsCreate(convertedData)
    .then((response: ApiResponse<ResponseModel<AddOnResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingAddOnsCreate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingAddOnsCreate as any).displayName
        );
      }
    });
}

export async function updateAddOn(
  addonId: string,
  addOnItemData: { id: string; name: string; nameAr: string; price: number; order?: number }
): Promise<AddOnResponse | ErrorInfo> {
  const payload = {
    set: {
      ...addOnItemData
    }
  };
  return api
    .furnishingAddOnsUpdate(addonId, payload)
    .then((response: ApiResponse<ResponseModel<AddOnResponse>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingAddOnsUpdate as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingAddOnsUpdate as any).displayName
        );
      }
    });
}

export async function deleteAddOn(addonId: string): Promise<any | ErrorInfo> {
  return api
    .furnishingAddOnsDelete(addonId)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        } else {
          return errorHelper(0, '', MODULE_NAME, (api.furnishingAddOnsDelete as any).displayName);
        }
      } else {
        return errorHelper(
          Number(response.status),
          response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
          MODULE_NAME,
          (api.furnishingAddOnsDelete as any).displayName
        );
      }
    });
}
